import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Progress,
  VStack,
  Text,
  Button,
  Box,
  useColorModeValue,
  HStack,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Image,
  Flex,
  Tooltip,
} from '@chakra-ui/react';
import { useWallet } from '../../contexts/WalletContext';
import Typography from '@/kit/antipad-ui/Typography';
import { Button as ButtonKit } from '@/kit/antipad-ui/Button';
import { spacing } from '@/kit/antipad-ui';

export interface TransactionDetail {
  symbol: string;
  icon: string;
  amount: number;
  usdValue: number;
  status?: 'pending' | 'success' | 'error' | 'warning';
  errorMessage?: string;
}

export interface TransactionStatus {
  text: string;
  status: 'pending' | 'success' | 'error' | 'warning';
  errorMessage?: string;
}

export interface CopyProgressModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCancel: () => void;
  currentStep: number;
  totalSteps: number;
  currentStatus: string;
  progress: number;
  details?: TransactionStatus[];
  isCompleted?: boolean;
  isError?: boolean;
  errorMessage?: string;
  successStats?: {
    totalTokens: number;
    totalAmount: number;
    successfulTransactions: number;
    failedTransactions: number;
  };
  onTryAgain?: () => void;
  onViewPortfolio?: () => void;
  currentTransaction: TransactionDetail | null;
  statusMessage?: string;
  statusType?: 'success' | 'warning' | 'error';
}

export const CopyProgressModal: React.FC<CopyProgressModalProps> = ({
  isOpen,
  onClose,
  onCancel,
  currentStep,
  totalSteps,
  currentStatus,
  progress,
  details = [],
  isCompleted = false,
  isError = false,
  errorMessage = '',
  successStats,
  onTryAgain,
  onViewPortfolio,
  currentTransaction,
  statusMessage,
  statusType,
}) => {
  // const bgColor = useColorModeValue('white', 'gray.800');
  const bgColor = "#1d1d1d";
  // const borderColor = useColorModeValue('gray.200', 'gray.600');
  const borderColor = 'rgba(60, 60, 60, 0.47)';
  const { publicKey } = useWallet();
  const detailsContainerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (detailsContainerRef.current) {
      detailsContainerRef.current.scrollTop = detailsContainerRef.current.scrollHeight;
    }
  }, [details]);

  const getStatusColor = (status?: 'pending' | 'success' | 'error' | 'warning') => {
    switch (status) {
      case 'success':
        return 'green.400';
      case 'error':
        return 'red.400';
      case 'warning':
        return 'yellow.400';
      case 'pending':
      default:
        return 'gray.400';
    }
  };

  const getStatusIcon = (status?: 'pending' | 'success' | 'error' | 'warning') => {
    switch (status) {
      case 'success':
        return '✓';
      case 'error':
        return '✕';
      case 'warning':
        return '⚠';
      case 'pending':
      default:
        return '';
    }
  };

  const renderContent = () => {
    if (isCompleted) {
      const finalStatusType = statusType || (successStats?.failedTransactions === 0 ? 'success' : successStats?.successfulTransactions === 0 ? 'warning' : 'warning');
      const finalStatusMessage = statusMessage ||
        (finalStatusType === 'success' ? 'Portfolio copied successfully!' :
          finalStatusType === 'warning' && successStats && successStats.successfulTransactions > 0 ? 'Portfolio copied partially' :
            'Failed to copy portfolio');

      return (
        <VStack spacing={4} align="stretch">
          <Alert status={finalStatusType} borderRadius="md">
            <AlertIcon />
            <Box>
              <AlertTitle>{finalStatusMessage}</AlertTitle>
              <AlertDescription>
                {successStats && (
                  <VStack align="stretch" mt={2} spacing={1}>
                    <Typography variant='small' className={spacing({ mb: '2x' })} color="var(--color-content-tertiary)">Total tokens processed: {successStats.totalTokens}</Typography>
                    <Typography variant='small' className={spacing({ mb: '2x' })} color="var(--color-content-tertiary)">Successful transactions: {successStats.successfulTransactions}</Typography>
                    <Typography variant='small' className={spacing({ mb: '2x' })} color="var(--color-content-tertiary)">Failed transactions: {successStats.failedTransactions}</Typography>
                    <Typography variant='small' className={spacing({ mb: '2x' })} color="var(--color-content-tertiary)">Total amount: ${successStats.totalAmount.toFixed(2)}</Typography>
                  </VStack>
                )}
              </AlertDescription>
            </Box>
          </Alert>
          <HStack spacing={4} justify="center">
            <ButtonKit onClick={onClose}>
              OK
            </ButtonKit>
            {publicKey && (
              <ButtonKit intent='brand' onClick={onViewPortfolio}>
                My Portfolio
              </ButtonKit>
            )}
          </HStack>
        </VStack>
      );
    }

    if (isError) {
      return (
        <VStack spacing={4} align="stretch">
          <Alert
            status={errorMessage === "User rejected the request." ? "info" : "error"}
            borderRadius="3xl"
            variant="subtle"
          >
            <AlertIcon />
            <Box>
              <AlertTitle>
                <Typography>
                  {errorMessage === "User rejected the request."
                    ? "Transaction cancelled"
                    : "Error occurred"
                  }
                </Typography>
              </AlertTitle>
              <AlertDescription>
                <Typography>
                  {errorMessage === "User rejected the request."
                    ? "You have rejected the transaction request in your wallet."
                    : errorMessage
                  }
                </Typography>
              </AlertDescription>
            </Box>
          </Alert>
          <HStack spacing={4} justify="center">
            <ButtonKit onClick={onClose}>
              Close
            </ButtonKit>
            {errorMessage !== "User rejected the request." && (
              <ButtonKit intent='destructive' onClick={onTryAgain}>
                Try Again
              </ButtonKit>
            )}
          </HStack>
        </VStack>
      );
    }

    return (
      <VStack spacing={4} align="stretch">
        <Box>
          <Typography variant='small' className={spacing({ mb: '2x' })} color="var(--color-content-tertiary)">
            Overall Progress
          </Typography>
          <Progress value={progress} colorScheme='brand' size="sm" />
        </Box>

        <Box>
          <Typography variant='small' className={spacing({ mb: '2x' })} color="var(--color-content-tertiary)">
            {currentStatus}
          </Typography>
          <Typography variant='small' className={spacing({ mb: '2x' })} color="var(--color-content-tertiary)">
            Step {currentStep} of {totalSteps}
          </Typography>
        </Box>

        {currentTransaction && (
          <Box
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="3xl"
            p={4}
            bg="#101010"
          >
            <HStack spacing={4} align="center">
              <Image
                src={currentTransaction.icon || `https://cryptologos.cc/logos/${currentTransaction.symbol.toLowerCase()}-${currentTransaction.symbol.toLowerCase()}-logo.png`}
                fallbackSrc="https://via.placeholder.com/40"
                boxSize="40px"
                borderRadius="full"
                alt={currentTransaction.symbol}
              />
              <VStack align="start" spacing={1} flex={1}>
                <HStack spacing={2}>
                  <Typography variant='medium_s'>
                    {currentTransaction.symbol}
                  </Typography>
                  <Typography
                    variant='small'
                    color={getStatusColor(currentTransaction.status)}
                  >
                    {getStatusIcon(currentTransaction.status)}
                  </Typography>
                </HStack>
                {currentTransaction.amount && (
                  <Typography variant='small' className={spacing({ mb: '2x' })} color="var(--color-content-tertiary)">
                    Amount: {currentTransaction.amount.toFixed(4)}
                  </Typography>
                )}
                {currentTransaction.usdValue && (
                  <Typography variant='small' className={spacing({ mb: '2x' })} color="var(--color-content-tertiary)">
                    Value: ${currentTransaction.usdValue.toFixed(2)}
                  </Typography>
                )}
                {currentTransaction.status === 'error' && currentTransaction.errorMessage && (
                  <Typography variant='small' className={spacing({ mb: '2x' })} color="var(--color-content-tertiary)">
                    Error: {currentTransaction.errorMessage}
                  </Typography>
                )}
              </VStack>
            </HStack>
          </Box>
        )}

        {details.length > 0 && (
          <Box
            ref={detailsContainerRef}
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="md"
            p={3}
            maxH="200px"
            overflowY="auto"
            css={{
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(160, 174, 192, 0.5)',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: 'rgba(160, 174, 192, 0.8)',
              },
            }}
          >
            <VStack align="stretch" spacing={2}>
              {details.map((detail, index) => {
                const isTransactionDetail = detail.text.startsWith('Preparing transaction for');
                return (
                  <HStack key={index} spacing={2} justify="space-between">
                    <Tooltip
                      label={detail.errorMessage}
                      isDisabled={!detail.errorMessage}
                    >
                      <Typography
                        variant='small'
                        className={spacing({ mb: '2x' })}
                        color={detail.status === 'error' ? 'red.400' : detail.status === 'warning' ? 'yellow.400' : 'inherit'}
                      >
                        {detail.text} {getStatusIcon(detail.status)}
                      </Typography>
                    </Tooltip>
                    {isTransactionDetail && (
                      <Box
                        w="8px"
                        h="8px"
                        borderRadius="full"
                        bg={getStatusColor(detail.status)}
                      />
                    )}
                  </HStack>
                );
              })}
            </VStack>
          </Box>
        )}

        <ButtonKit
          size='s'
          onClick={onClose}
          fullWidth
        >
          Close
        </ButtonKit>
      </VStack>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="md" closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent bg={bgColor}>
        <ModalHeader>
          <Typography variant='3xLarge_heading'>
            {isCompleted ? 'Copy Completed' : isError ? 'Copy Failed' : 'Portfolio Copying'}
          </Typography>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          {renderContent()}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}; 